import { BUSINESS_TYPE_LABELS, BUSINESS_TYPES } from '@crm/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Select,
  Spinner,
} from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Building2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormError, FormField, FormSuccess, PageHeader, PhotoField } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { useCompany, useUpdateCompany } from '../hooks';

const companyFormSchema = z.object({
  name: z.string().min(2, 'Informe o nome').max(120),
  legalName: z.string().max(160),
  document: z.string().max(20).regex(/^\d*$/, 'Informe apenas numeros'),
  businessType: z.enum(BUSINESS_TYPES),
  phone: z.string().max(40),
  logo: z.string().max(2048),
  /** Zero desliga a desistencia automatica. */
  noShowAfterMinutes: z.coerce
    .number()
    .int()
    .min(0)
    .max(24 * 60),
});

type CompanyFormValues = z.infer<typeof companyFormSchema>;

export function CompanyPage() {
  useDocumentTitle('Empresa');

  const canWrite = useCan('settings', 'WRITE');
  const company = useCompany();
  const updateCompany = useUpdateCompany();

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CompanyFormValues>({
    resolver: zodResolver(companyFormSchema),
    values: company.data
      ? {
          name: company.data.name,
          legalName: company.data.legalName ?? '',
          document: company.data.document ?? '',
          businessType: company.data.businessType,
          phone: company.data.phone ?? '',
          logo: company.data.logo ?? '',
          noShowAfterMinutes: company.data.noShowAfterMinutes ?? 0,
        }
      : undefined,
    defaultValues: {
      name: '',
      legalName: '',
      document: '',
      businessType: 'BEAUTY_SALON',
      phone: '',
      logo: '',
      noShowAfterMinutes: 0,
    },
  });

  const logo = watch('logo');

  if (company.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  if (company.isError) {
    return <p className="text-sm text-destructive">{toErrorMessage(company.error)}</p>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Building2 />}
        title="Empresa"
        description="Dados do seu tenant: todo cadastro do sistema pertence a esta empresa."
        actions={
          company.data ? (
            <Badge variant="secondary">
              {company.data.unitsCount} {company.data.unitsCount === 1 ? 'unidade' : 'unidades'}
            </Badge>
          ) : null
        }
      />

      <Card variant="flush" className="mx-auto max-w-2xl">
        <CardHeader>
          <CardTitle className="text-base">Dados cadastrais</CardTitle>
          <CardDescription>
            O ramo define o vocabulario do sistema em telas futuras (agenda e atendimentos).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={handleSubmit((values) => updateCompany.mutate(values))}
            noValidate
          >
            <FormError message={updateCompany.error ? toErrorMessage(updateCompany.error) : null} />

            {updateCompany.isSuccess ? <FormSuccess message="Empresa atualizada." /> : null}

            <div className="flex items-center gap-4">
              {logo ? (
                <img
                  src={logo}
                  alt="Logo da empresa"
                  className="h-16 w-16 rounded-lg border border-border object-cover"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-muted">
                  <Building2 className="h-7 w-7 text-muted-foreground" />
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                A logo vem de uma URL publica; deixe em branco para usar o icone padrao.
              </p>
            </div>

            <FormField
              label="Nome fantasia"
              disabled={!canWrite}
              error={errors.name?.message}
              {...register('name')}
            />

            <FormField
              label="Razao social"
              disabled={!canWrite}
              error={errors.legalName?.message}
              {...register('legalName')}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label="CNPJ"
                placeholder="Somente numeros"
                disabled={!canWrite}
                error={errors.document?.message}
                {...register('document')}
              />

              <FormField
                label="Telefone"
                type="tel"
                disabled={!canWrite}
                error={errors.phone?.message}
                {...register('phone')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessType">Ramo</Label>
              <Select id="businessType" disabled={!canWrite} {...register('businessType')}>
                {BUSINESS_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {BUSINESS_TYPE_LABELS[type]}
                  </option>
                ))}
              </Select>
            </div>

            <PhotoField
              label="Logo"
              shape="square"
              value={watch('logo') ?? ''}
              onChange={(valor) => setValue('logo', valor, { shouldDirty: true })}
            />

            <div className="space-y-2">
              <FormField
                label="Desistencia automatica (minutos de atraso)"
                type="number"
                min="0"
                inputMode="numeric"
                disabled={!canWrite}
                error={errors.noShowAfterMinutes?.message}
                {...register('noShowAfterMinutes')}
              />
              <p className="text-xs text-muted-foreground">
                Passado esse tempo do horario marcado, quem nao fez check-in vira desistencia
                sozinho e o horario e liberado. Zero desliga a regra.
              </p>
            </div>

            {canWrite ? (
              <Button type="submit" disabled={updateCompany.isPending}>
                {updateCompany.isPending ? <Spinner /> : null}
                Salvar alteracoes
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Voce tem acesso de consulta nesta area.
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
